import React, { Suspense } from 'react';
import { useLocation } from 'react-router';
import { IntercomProvider } from 'react-use-intercom';
import { useLocalStorage } from 'usehooks-ts';
import { Content, Main, PageLayout, TopNavigation } from '@atlaskit/page-layout';
import { Grid } from '@atlaskit/primitives';
import { setGlobalTheme } from '@atlaskit/tokens';
import { PRODUCTION, getEnvironment } from '@atlassian/temp-shared';
import { Sidebar, StoreRecentlyViewed, useFeatureToggle, LeftSidebar } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { AddCustomerModal } from '@css-xp/ppc-add-customer-modal';

import {
  MAX_APP_STORAGE,
  MAX_ENTITY_STORAGE,
  ENABLE_DESIGN_ITERATION_1,
  STATSIG_FEATURE_GATES,
  PARTNER_CUSTOMER_ADVOCATE_INTERCOM_APP_ID,
  PCA_TESTING_WORKSPACE_INTERCOM_APP_ID,
} from '@css-xp/ppc-constants';
import { APP_PPC } from '@atlassian/temp-client-utils';
import Notifications from '@css-xp/base/notifications/components/notifications';
import { NavigationBar } from '@css-xp/ppc-navigation';
import { PartnerApplicationContext, PartnerDomainContextProvider } from '@css-xp/ppc-user-context';
import { initUFO } from '@css-xp/ppc-ufo';
import '@css-xp/base/assets/css/all-styles.scss';
import { SsrComponent } from '@css-xp/ppc-bro-base';
import { FeatureGatesInitialize } from '@css-xp/ppc-feature-gate-context';
import { GlobalApiManager } from './global-api-manager';
import { routesWithoutNavigations } from './routes-without-navigation';
import { NotificationComponent } from './notification';
import { IntercomBoot } from './intercom';
import { PostOfficeProvider } from './post-office';
import './App.css';

initUFO();

const pathsWithOutNavigation = routesWithoutNavigations.map((route) => route.path);

const AppContent = ({ children }: React.PropsWithChildren<Record<string, unknown>>) => {
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const userProfile = useApplicationContext();
  const isSsrEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_REACT_SSR_MVP);
  const isSoldToAddressArchivalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ARCHIVE_SOLD_TO_ADDRESS);

  const [selectedPartnerAccount] = useLocalStorage(`${userProfile?.atlassianAccountId}-selectedTxa`, '');
  if (!isDesignIteration1Enabled) {
    return (
      <Content>
        <Main id="main-content">{children}</Main>
        <div id="in-product-help-attach" />
        <Sidebar />
      </Content>
    );
  }

  setGlobalTheme({
    colorMode: 'light',
    typography: 'typography-modernized', // Added to get variables such as font.weight.semibold and font.heading.small
  });

  return (
    <Content>
      {isSsrEnabled ? (
        <Suspense fallback={<LeftSidebar />}>
          <SsrComponent route={'left-sidebar'} txaId={selectedPartnerAccount} fallback={<LeftSidebar />} />
        </Suspense>
      ) : (
        <LeftSidebar />
      )}
      <Grid
        id="grid-container"
        gap="space.200"
        autoFlow="column"
        templateAreas={[
          'main-content main-content main-content main-content main-content main-content main-content main-content main-content main-content main-content main-content',
        ]}
      >
        <Main id="main-content-v2">{children}</Main>
      </Grid>
      <Sidebar />
      <div id="in-product-help-attach" />
      <AddCustomerModal isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled} />
    </Content>
  );
};

const App = ({ children }: React.PropsWithChildren<Record<string, unknown>>) => {
  const location = useLocation();

  const INTERCOM_APP_ID =
    getEnvironment() === PRODUCTION ? PARTNER_CUSTOMER_ADVOCATE_INTERCOM_APP_ID : PCA_TESTING_WORKSPACE_INTERCOM_APP_ID;

  return (
    <PartnerApplicationContext>
      <PostOfficeProvider>
        <IntercomProvider appId={INTERCOM_APP_ID}>
          <FeatureGatesInitialize>
            <PartnerDomainContextProvider>
              <IntercomBoot>
                <StoreRecentlyViewed
                  appName={APP_PPC}
                  trackTitle={false}
                  enableUserIdAsStoragePrefix={true}
                  maxPerAppCount={MAX_APP_STORAGE}
                  maxPerEntityCount={MAX_ENTITY_STORAGE}
                />
                <GlobalApiManager />
                <PageLayout testId="app-layout">
                  {!pathsWithOutNavigation.includes(location?.pathname) && (
                    <TopNavigation isFixed={true}>
                      <NavigationBar NotificationComponent={NotificationComponent} />
                    </TopNavigation>
                  )}
                  <AppContent>{children}</AppContent>
                  <Notifications />
                </PageLayout>
              </IntercomBoot>
            </PartnerDomainContextProvider>
          </FeatureGatesInitialize>
        </IntercomProvider>
      </PostOfficeProvider>
    </PartnerApplicationContext>
  );
};
export default App;
